import { useState, useEffect, useMemo } from 'react';
import { useAxios } from '../../contexts/axios';
import OvertimeDescription from '../table/overtimeDescription';
import DD2 from '../DD2';
import PrimaryButton from '../button/PrimaryButton';
import DatePickerInput from '../DatePickerInput';
import FlexRow from '../layout/FlexRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TabPanel from '../tabs/tabPanel';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Switch,
} from '@mui/material';
import MaterialReactTable from 'material-react-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ExportToCsv } from 'export-to-csv';
import Loading from './Loading';
import {
    DIMENSION,
    groupBy,
    DAILY_PERIODICITY_MAX_INCLUSIVE_DAYS,
    API_MIN_DATE,
    countInclusiveCalendarDays,
    makeServiceCostingA11yProps,
    headerRowLooksWeekly,
    ParseDate,
    TableDataCell,
    TableFooterDataCell,
    TableFirstDataCell,
    alignOrgWiseRowsToUnionColumns,
    normalizeCostAnalysisRows,
    clubOrgWiseOtherServices,
    periodicities,
} from './org-wise-costing-shared';

const COST_ANALYSIS_OVERALL_PATH = '/cost-analysis/overall';

const verticalOptions = [
    { name: 'BM', id: 'BM' },
    { name: 'MARP', id: 'MARP' },
];

const a11yProps = makeServiceCostingA11yProps('org-wise');

export default function OrgWiseCostingForm() {
    const [loading, setLoading] = useState(false);
    const [fetchedCostRows, setFetchedCostRows] = useState([]);
    const [finalData, setFinalData] = useState([]);
    const [totalData, setTotalData] = useState(null);
    const [header, setHeader] = useState([]);
    const [totalCost, setTotalCost] = useState([]);
    const [column, setColumn] = useState([]);
    const [ok, setOk] = useState(false);
    const authAxios = useAxios();
    const [value, setValue] = useState(0);
    const [showServiceBreakdown, setShowServiceBreakdown] = useState(false);
    const [dailyRangeLimitDialogOpen, setDailyRangeLimitDialogOpen] =
        useState(false);
    const [clubOtherCosts, setClubOtherCosts] = useState(false);

    var endDateMaxLimit = new Date();
    endDateMaxLimit.setDate(endDateMaxLimit.getDate() - 2);

    const [vertical, setVertical] = useState(null);
    const [periodicity, setPeriodicity] = useState(null);
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 2);
        return d;
    });
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 2);
        return d;
    });

    const data = useMemo(
        () => clubOrgWiseOtherServices(fetchedCostRows, clubOtherCosts),
        [fetchedCostRows, clubOtherCosts]
    );

    const csvOptions = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        useBom: true,
        useKeysAsHeaders: false,
        headers: column.map((c) => c.header),
    };
    const csvExporter = new ExportToCsv(csvOptions);

    const handleExportData = () => {
        csvExporter.generateCsv(data);
    };

    const isInputValid = () => {
        if (!vertical) {
            alert('Please select a vertical');
            return false;
        }
        if (!periodicity) {
            alert('Please select a periodicity for data');
            return false;
        }
        if (startDate > endDate) {
            alert('Please enter valid start and end date');
            return false;
        }
        if (periodicity.id === 'daily') {
            const span = countInclusiveCalendarDays(startDate, endDate);
            if (span > DAILY_PERIODICITY_MAX_INCLUSIVE_DAYS) {
                setDailyRangeLimitDialogOpen(true);
                return false;
            }
        }
        return true;
    };

    const getOrgWiseCostingData = async () => {
        if (!isInputValid() || loading) {
            return;
        }

        setLoading(true);
        try {
            const { data: responseData } = await authAxios.get(
                COST_ANALYSIS_OVERALL_PATH,
                {
                    params: {
                        startDate: startDate.toISOString().split('T')[0],
                        endDate: endDate.toISOString().split('T')[0],
                        periodicity: periodicity.id,
                        vertical: vertical.id,
                    },
                }
            );
            let list = responseData;
            if (!Array.isArray(list) && Array.isArray(responseData?.data)) {
                list = responseData.data;
            }
            if (!Array.isArray(list)) {
                setFetchedCostRows([]);
                return;
            }
            setFetchedCostRows(
                alignOrgWiseRowsToUnionColumns(
                    normalizeCostAnalysisRows(list),
                    DIMENSION
                )
            );
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (data?.length > 0) {
            setOk('load');
            const head = Object.keys(data[0]);
            setHeader(head);
            const Cost = [];
            for (var i = 0; i < data.length; i++) {
                var ServiceCostData = [];
                ServiceCostData.push(data[i][DIMENSION]);
                ServiceCostData.push([0, data[i][head[1]]]);
                for (var j = 2; j < head.length; j++) {
                    ServiceCostData.push([
                        data[i][head[j - 1]] !== 0
                            ? ((data[i][head[j]] - data[i][head[j - 1]]) *
                                  100) /
                              data[i][head[j - 1]]
                            : data[i][head[j]] !== 0
                            ? 'kickstart'
                            : 0,
                        data[i][head[j]],
                    ]);
                }
                Cost.push(ServiceCostData);
            }

            const total = [];
            let totalCostArr = [];
            total.push('Total');
            for (var ti = 1; ti < Cost[0].length; ti++) {
                var TotalServiceCost = 0;
                var TotalServiceCostPrevDay = 0;
                if (ti > 1) {
                    for (var r = 0; r < Cost.length; r++) {
                        TotalServiceCost = TotalServiceCost + Cost[r][ti][1];
                        TotalServiceCostPrevDay =
                            TotalServiceCostPrevDay + Cost[r][ti - 1][1];
                    }
                    total.push([
                        TotalServiceCostPrevDay !== 0
                            ? ((TotalServiceCost - TotalServiceCostPrevDay) *
                                  100) /
                              TotalServiceCostPrevDay
                            : TotalServiceCost !== 0
                            ? 'kickstart'
                            : 0,
                        TotalServiceCost,
                    ]);
                } else {
                    for (var r2 = 0; r2 < Cost.length; r2++) {
                        TotalServiceCost = TotalServiceCost + Cost[r2][ti][1];
                    }
                    total.push([0, TotalServiceCost]);
                }
                totalCostArr.push(TotalServiceCost);
            }
            setTotalCost(total);
            setTotalData(totalCostArr);

            const finalRows = [];
            for (var fr = 0; fr < Cost.length; fr++) {
                var rowData = {};
                for (var c = 0; c < head.length; c++) {
                    rowData[head[c]] = Cost[fr][c];
                }
                finalRows.push(rowData);
            }
            setFinalData(finalRows);
            setOk('true');
        } else {
            setFinalData([]);
            setHeader([]);
            setTotalCost([]);
            setColumn([]);
            setTotalData(null);
            setOk(false);
        }
    }, [data]);

    useEffect(() => {
        if (header.length > 0 && totalCost.length > 0 && data.length > 0) {
            var cols = [
                {
                    accessorKey: header[0],
                    header: header[0],
                    Header: (
                        <div
                            style={{
                                textAlign: 'center',
                                fontSize: '15px',
                            }}
                        >
                            {header[0].toUpperCase()}
                        </div>
                    ),
                    muiTableHeadCellProps: { align: 'center' },
                    muiTableBodyCellProps: { align: 'center' },
                    Cell: ({ cell }) => (
                        <TableFirstDataCell>
                            {cell.getValue()}
                        </TableFirstDataCell>
                    ),
                    Footer: (
                        <TableFirstDataCell>{'TOTAL COST'}</TableFirstDataCell>
                    ),
                },
            ];
            if (
                periodicity?.id === 'weekly' &&
                headerRowLooksWeekly(header)
            ) {
                const customHeader = [];
                for (var hi = 0; hi < header.length; hi++) {
                    customHeader.push(header[hi].split(' ').filter(Boolean));
                }

                for (var ci = 1; ci < header.length; ci++) {
                    const weekLabel =
                        customHeader[ci][0] &&
                        customHeader[ci][0].includes('-')
                            ? customHeader[ci][0].split('-')[1]
                            : customHeader[ci][0];
                    cols.push({
                        accessorKey: header[ci],
                        header: header[ci],
                        Header: (
                            <div
                                style={{
                                    textAlign: 'center',
                                    fontSize: '15px',
                                }}
                                className="grid grid-rows-2 grid-flow-col gap-1"
                            >
                                <div>{'Week ' + weekLabel}</div>
                                <div>
                                    {ParseDate(customHeader[ci][1]) +
                                        ' - ' +
                                        ParseDate(customHeader[ci][2])}
                                </div>
                            </div>
                        ),
                        sortingFn: 'myCustomSortingFn',
                        muiTableHeadCellProps: { align: 'center' },
                        muiTableBodyCellProps: { align: 'center' },
                        Cell: ({ cell }) => (
                            <TableDataCell
                                cost={cell.getValue()[1]}
                                increment={cell.getValue()[0]}
                            />
                        ),
                        Footer: (
                            <TableFooterDataCell
                                cost={totalCost[ci][1]}
                                increment={totalCost[ci][0]}
                            />
                        ),
                    });
                }
            } else {
                for (var di = 1; di < header.length; di++) {
                    cols.push({
                        accessorKey: header[di],
                        header: header[di],
                        Header: (
                            <div
                                style={{
                                    textAlign: 'center',
                                    fontSize: '15px',
                                }}
                            >
                                {header[di].toUpperCase()}
                            </div>
                        ),
                        sortingFn: 'myCustomSortingFn',
                        muiTableHeadCellProps: { align: 'center' },
                        muiTableBodyCellProps: { align: 'center' },
                        Cell: ({ cell }) => (
                            <TableDataCell
                                cost={cell.getValue()[1]}
                                increment={cell.getValue()[0]}
                            />
                        ),
                        Footer: (
                            <TableFooterDataCell
                                cost={totalCost[di][1]}
                                increment={totalCost[di][0]}
                            />
                        ),
                    });
                }
            }
            setColumn(cols);
        }
    }, [totalCost, header, data.length, periodicity?.id]);

    useEffect(() => {
        setFetchedCostRows([]);
        setFinalData([]);
        setHeader([]);
        setTotalCost([]);
        setColumn([]);
        setTotalData(null);
        setOk(false);
        setValue(0);
        setShowServiceBreakdown(false);
    }, [startDate, endDate, vertical, periodicity]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const graphPeriodCount =
        data.length > 0 && data[0]
            ? Math.max(0, Object.keys(data[0]).length - 1)
            : 0;
    const graphChartReady =
        graphPeriodCount > 0 &&
        Array.isArray(totalData) &&
        totalData.length === graphPeriodCount;

    return (
        <>
            <Dialog
                open={dailyRangeLimitDialogOpen}
                onClose={() => setDailyRangeLimitDialogOpen(false)}
                aria-labelledby="daily-range-limit-title"
            >
                <DialogTitle id="daily-range-limit-title">
                    Date range too large
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        For daily periodicity, the maximum allowed difference
                        between start and end date is{' '}
                        {DAILY_PERIODICITY_MAX_INCLUSIVE_DAYS} calendar days
                        (inclusive). Please narrow your range and try again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDailyRangeLimitDialogOpen(false)}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {!!loading ? (
                <Box sx={{ width: '100%', marginTop: '40vh' }}>
                    <LinearProgress />
                </Box>
            ) : (
                <>
                    <FlexRow>
                        <DD2
                            label={'Vertical'}
                            emptyLabel={'Select a vertical'}
                            options={verticalOptions}
                            value={vertical}
                            onChange={setVertical}
                            displayProperty={'name'}
                            disabled={false}
                        />
                        <DD2
                            label={'Periodicity'}
                            emptyLabel={'Select a periodicity'}
                            options={periodicities}
                            value={periodicity}
                            onChange={setPeriodicity}
                            displayProperty={'name'}
                            disabled={!!!vertical}
                        />
                        <FlexRow />
                    </FlexRow>
                    <FlexRow>
                        <DatePickerInput
                            label={'Start Date'}
                            value={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            minDate={API_MIN_DATE}
                        />
                        <DatePickerInput
                            label={'End Date'}
                            value={endDate}
                            onChange={setEndDate}
                            maxDate={endDateMaxLimit}
                            minDate={startDate}
                        />
                        <FlexRow></FlexRow>
                        <FlexRow></FlexRow>
                    </FlexRow>
                    <div className="mt-8">
                        <PrimaryButton
                            title={'$ Get Org-wise Costing Data'}
                            onClick={getOrgWiseCostingData}
                        />
                    </div>
                    {data.length > 0 ? (
                        <Box sx={{ width: '100%', marginTop: '30px' }}>
                            <FormControlLabel
                                className="mb-2 block"
                                control={
                                    <Switch
                                        checked={clubOtherCosts}
                                        onChange={(e) =>
                                            setClubOtherCosts(e.target.checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label="Group all services into GCP"
                            />
                            <Box
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="org-wise costing tabs"
                                >
                                    <Tab label="TABLE" {...a11yProps(0)} />
                                    <Tab label="GRAPH" {...a11yProps(1)} />
                                </Tabs>
                            </Box>
                            <TabPanel value={value} index={0}>
                                {(() => {
                                    switch (ok) {
                                        case false:
                                        case 'false':
                                            return <></>;
                                        case 'load':
                                            return <Loading />;
                                        case 'true':
                                            return (
                                                <div
                                                    style={{
                                                        marginTop: '30px',
                                                    }}
                                                >
                                                    <MaterialReactTable
                                                        data={finalData}
                                                        columns={column}
                                                        enableDensityToggle={
                                                            false
                                                        }
                                                        enableFullScreenToggle
                                                        enableStickyFooter
                                                        enableStickyHeader
                                                        enableRowActions={false}
                                                        muiTableBodyCellProps={{
                                                            sx: {
                                                                fontWeight:
                                                                    'bold',
                                                                fontSize:
                                                                    '14px',
                                                            },
                                                        }}
                                                        muiTableBodyProps={{
                                                            sx: {
                                                                marginTop:
                                                                    '100px',
                                                            },
                                                        }}
                                                        sortingFns={{
                                                            myCustomSortingFn: (
                                                                rowA,
                                                                rowB,
                                                                columnId
                                                            ) =>
                                                                rowA.getValue(
                                                                    columnId
                                                                )[1] >
                                                                rowB.getValue(
                                                                    columnId
                                                                )[1]
                                                                    ? 1
                                                                    : -1,
                                                        }}
                                                        initialState={{
                                                            columnPinning: {
                                                                left: [],
                                                                right: [
                                                                    DIMENSION,
                                                                ],
                                                            },
                                                            density: 'compact',
                                                        }}
                                                        positionToolbarAlertBanner="bottom"
                                                        renderTopToolbarCustomActions={() => (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: '1rem',
                                                                    p: '0.5rem',
                                                                    flexWrap:
                                                                        'wrap',
                                                                }}
                                                            >
                                                                <Button
                                                                    color="primary"
                                                                    onClick={
                                                                        handleExportData
                                                                    }
                                                                    startIcon={
                                                                        <FileDownloadIcon />
                                                                    }
                                                                    variant="contained"
                                                                >
                                                                    Export All
                                                                    Data
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    />
                                                </div>
                                            );
                                        default:
                                            return <></>;
                                    }
                                })()}
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <div className="mt-8 min-w-[1500px] p-4">
                                    {graphChartReady ? (
                                        <>
                                            <FormControlLabel
                                                className="mb-4 block"
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            showServiceBreakdown
                                                        }
                                                        onChange={(e) =>
                                                            setShowServiceBreakdown(
                                                                e.target.checked
                                                            )
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label="Show cost breakdown by service (stacked columns)"
                                            />
                                            <OvertimeDescription
                                                data={data}
                                                groupBy={groupBy.id}
                                                groupByName={groupBy.name}
                                                periodicity={periodicity?.id}
                                                totalCost={totalData}
                                                alwaysColumnTotal
                                                showServiceBreakdown={
                                                    showServiceBreakdown
                                                }
                                                enableBreakdownForDaily
                                            />
                                        </>
                                    ) : (
                                        <Box sx={{ marginTop: '24px' }}>
                                            <Loading />
                                        </Box>
                                    )}
                                </div>
                            </TabPanel>
                        </Box>
                    ) : (
                        <></>
                    )}
                </>
            )}
        </>
    );
}
